import * as React from 'react'
import { ForwardedRef, useEffect, useState } from 'react'
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form'
import { useMergedRef } from '@mantine/hooks'
import { Icon, IconName } from './Icon'
import { OnClickProps } from '../utils/onClickProps'
import { Fonts } from '../style/fonts'
import { Colors } from '../style/colors'
import { Sizes } from '../style/tokens'

const inputButtonStyle = {
  padding: 0,
  margin: 0,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  alignItems: 'center',
  display: 'inline-flex',
}

export type BaseInputProps = React.ComponentProps<'input'> & {
  iconRight?: IconName
  iconRightColor?: string
  textRight?: string
  label?: string
  labelIsPlaceholder?: boolean
  iconLeft?: IconName
  iconLeftColor?: string
  error?: string | React.ReactNode
  isError?: boolean
  helper?: string | React.ReactNode
  isLabelStatic?: boolean
  internalInputRenderer?: (inputComponent: React.ReactNode) => React.ReactNode
  isMessageBlock?: boolean
  variant?: 'regular' | 'small'
  isClearable?: boolean
  onClear?: () => void
  additionalAction?: {
    icon: IconName
    disabled?: boolean
    renderer?: (actionComponent: React.ReactNode) => React.ReactNode
  } & OnClickProps
}

export const BaseInputComponent = (
  {
    className,
    label,
    labelIsPlaceholder,
    isLabelStatic = false,
    iconLeft,
    iconRight,
    textRight,
    onChange,
    value,
    error,
    isError,
    helper,
    internalInputRenderer,
    isMessageBlock,
    iconRightColor = '#6B7280',
    iconLeftColor = '#6B7280',
    additionalAction,
    onClear,
    isClearable,
    variant = 'regular',
    ...props
  }: BaseInputProps,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const hasValueFor = (value: BaseInputProps['value'], isLabelStatic?: boolean) => isLabelStatic || value === 0 || !!value

  const [hasValue, setHasValue] = useState(hasValueFor(value, isLabelStatic))
  const [inputHover, setInputHover] = useState(false)
  const [inputFocus, setInputFocus] = useState(false)
  const [inputWidth, setInputWidth] = useState(0)

  const [previousValue, setPreviousValue] = useState(value)
  const [previousIsLabelStatic, setPreviousIsLabelStatic] = useState(isLabelStatic)

  // hasValue follows the controlled value, but onChange also drives it for uncontrolled inputs:
  // realign on change, during render rather than in an effect
  if (previousValue !== value || previousIsLabelStatic !== isLabelStatic) {
    setPreviousValue(value)
    setPreviousIsLabelStatic(isLabelStatic)
    setHasValue(hasValueFor(value, isLabelStatic))
  }

  const localInputRef = React.useRef<HTMLInputElement>(null)
  useEffect(() => {
    // automatically set the width of the label to the width of the input
    setInputWidth(localInputRef.current?.offsetWidth ?? 0)
  }, [])

  const inputStyle = {
    border: 'none',
    boxSizing: 'border-box' as const,
    ...(variant === 'small' ? Fonts.P2 : Fonts.P1),
    margin: 0,
    flex: '1 1 auto',
    borderRadius: '3px',
    color: Colors.Jet,
    padding: variant === 'small' ? '4px 0' : '10px 0',
    minWidth: 0,
    ...(inputFocus && {
      outline: 'none',
    }),
    ...props,
  }

  const labelStyle = {
    fontWeight: 400,
    fontStyle: 'italic' as const,
    ...Fonts.P1,
    position: 'absolute' as const,
    top: variant === 'small' ? '4px' : '10px',
    ...(variant === 'small' ? { left: '5px' } : {}),

    ...Fonts.colors.SlateGrey,
    ...((error || isError) && { color: Colors.Alert }),
    pointerEvents: 'none' as const,
    transition: '400ms cubic-bezier(0, 0, 0.2, 1) 0ms',
    transitionProperty: 'top, left, font-size, font-style, padding-left',
    textOverflow: 'ellipsis' as const,
    overflow: 'hidden' as const,
    whiteSpace: 'nowrap' as const,
    width: `${inputWidth}px`,
    ...(iconLeft ? { left: variant === 'small' ? '30px' : '38px' } : {}),
    ...(labelIsPlaceholder
      ? hasValue && { display: 'none' as const }
      : inputFocus || hasValue
        ? {
            top: '-5px',
            left: '10px',
            fontSize: `${value === 'small' ? Sizes[10] : Sizes[12]}`,
            fontStyle: 'normal',
            lineHeight: '12px',
            paddingLeft: '5px',
          }
        : {}),
  }

  const getBorderColor = () => {
    if (error || isError) return Colors.Alert
    if (inputFocus) return Colors.King
    return inputHover ? Colors.Silver : Colors.Platinum
  }

  const clearAction = onClear ?? (() => onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>))

  const isClearButtonVisible = !props.disabled && (isClearable === undefined ? true : isClearable)

  const internalInput = (
    <input
      {...props}
      ref={useMergedRef(localInputRef, ref)}
      value={value}
      style={inputStyle}
      onChange={(e) => {
        if (!isLabelStatic) setHasValue(!!e.target.value)
        onChange?.(e)
      }}
      onFocus={(e) => {
        setInputFocus(true)
        props.onFocus?.(e)
      }}
      onBlur={(e) => {
        setInputFocus(false)
        props.onBlur?.(e)
      }}
      onMouseEnter={(e) => {
        setInputHover(true)
        props.onMouseEnter?.(e)
      }}
      onMouseLeave={(e) => {
        setInputHover(false)
        props.onMouseLeave?.(e)
      }}
    />
  )

  const actionButtonStyle = {
    ...inputButtonStyle,
    ...(additionalAction?.disabled && {
      cursor: 'default',
    }),
  }

  const additionalActionComponent = additionalAction && (
    <button
      type="button"
      style={actionButtonStyle}
      className="action-btn"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        additionalAction.onClick(e)
      }}
    >
      <Icon name={additionalAction.icon} />
    </button>
  )

  const textInputWrapperStyle = {
    display: 'flex',
    position: 'relative' as const,
    alignItems: 'center' as const,
    gap: '2px',
    padding: `0 ${variant === 'small' ? '8px' : '12px'}`,
    backgroundColor: Colors.White,
    boxSizing: 'content-box' as const,
    justifyContent: 'space-between' as const,
    ...(additionalAction?.disabled ? { backgroundColor: Colors.Ghost } : {}),
  }

  const iconStyle =
    variant === 'small'
      ? {
          minWidth: '1.25rem',
          width: '1.25rem',
          minHeight: '1.25rem',
          height: '1.25rem',
        }
      : {}

  const lengendStyle = {
    visibility: 'hidden' as const,
    maxWidth: '0.01px',
    float: 'unset' as const,
    overflow: 'hidden' as const,
    display: 'block' as const,
    width: 'auto',
    padding: 0,
    height: '11px',
    fontSize: variant === 'small' ? Sizes[10] : Sizes[12],
    lineHeight: '12px',
    transition: 'max-width 200ms cubic-bezier(0, 0, 0.2, 1) 0ms',
    whiteSpace: 'nowrap' as const,
    ...(inputFocus || hasValue ? { maxWidth: '100%' } : {}),
  }

  const legendLabelStyle = {
    padding: label ? '0 4px' : 0,
    ...(inputFocus && hasValue && { maxWidth: '100%' }),
    ...((!label || labelIsPlaceholder) && { display: 'none' as const }),
  }

  const clearButtonStyle = {
    visibility: 'hidden' as const,
    color: Colors.Hurricane,
    ...(variant === 'small'
      ? {
          minWidth: '1.25rem',
          width: '1.25rem',
          minHeight: '1.25rem',
          height: '1.25rem',
        }
      : {}),
    ...((inputHover || hasValue) && {
      visibility: 'visible' as const,
      color: Colors.Ash,
    }),
  }

  return (
    <div className={className} onMouseEnter={() => setInputHover(true)} onMouseLeave={() => setInputHover(false)}>
      <div style={textInputWrapperStyle}>
        {iconLeft && <Icon fill={iconLeftColor} color={iconLeftColor} name={iconLeft} size="md" style={iconStyle} />}
        {internalInputRenderer ? internalInputRenderer(internalInput) : internalInput}
        <label style={labelStyle}>{label}</label>
        {isClearButtonVisible && (
          <button
            type="button"
            className="flex items-center"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              clearAction()
            }}
            onMouseEnter={() => {
              setInputHover(true)
            }}
            onMouseLeave={() => {
              setInputHover(false)
            }}
          >
            <Icon name="close" style={{ ...iconStyle, ...clearButtonStyle, cursor: 'pointer' }} />
          </button>
        )}

        {additionalAction && (additionalAction.renderer ? additionalAction.renderer(additionalActionComponent) : additionalActionComponent)}
        {iconRight && <Icon fill={iconRightColor} color={iconRightColor} name={iconRight} size="lg" />}
        {textRight && <span style={{ textAlign: 'right', color: Colors.SlateGrey }}>{textRight}</span>}

        <fieldset
          aria-hidden={'true'}
          className="pointer-events-none absolute -top-1.25 bottom-0 left-0 right-0 m-0 rounded-[3px] border border-solid px-2.5"
          style={{ borderColor: getBorderColor() }}
        >
          <legend style={lengendStyle}>
            <span className={'legend-label'} style={legendLabelStyle}>
              {' '}
              {!labelIsPlaceholder && label}
            </span>
          </legend>
        </fieldset>
      </div>

      <div style={{ position: 'relative' }}>
        {error ? (
          <span className="text-sm flex items-center gap-1 mt-0.5" style={{ color: Colors.Alert }}>
            <Icon name="alert" size="sm" color={Colors.Alert} style={iconStyle} />
            {error}
          </span>
        ) : (
          helper && <span style={{ marginLeft: '12px', color: Colors.SlateGrey }}>{helper}</span>
        )}
      </div>
    </div>
  )
}

export const BaseInput = React.forwardRef(BaseInputComponent)

type InputProps<T extends FieldValues> = Omit<UseControllerProps<T>, 'defaultValue'> & Omit<BaseInputProps, 'value' | 'ref'>

export const Input = <T extends FieldValues>({ name, control, rules, shouldUnregister, onChange, type, ...props }: InputProps<T>) => {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      shouldUnregister={shouldUnregister}
      render={({ field }) => (
        <BaseInput
          {...props}
          name={field.name}
          type={type}
          value={field.value as string}
          onChange={(v) => {
            let value: any = v.target.value
            // A cleared number field stays empty, so `required` still catches it: `+''` would be 0.
            if (type === 'number' && v.target.value !== '') value = +v.target.value
            field.onChange(value)
            onChange?.(v)
          }}
        />
      )}
    />
  )
}
